/**
 * GenAI Summary Component
 * 
 * Component that displays GenAI maintenance analysis with interactive
 * start/refresh functionality and structured analysis results.
 */

import React from 'react';
import { css } from '@emotion/css';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useGenAISummary } from '../../../hooks/use-genai-summary';
import { WorkOrderEvent } from '../../../services/equipment/types';
import { AnalysisResults } from './analysis-results';

interface GenAISummaryProps {
  /** Work order events from timeline */
  events?: WorkOrderEvent[];
  /** Whether timeline is loading */
  timelineLoading?: boolean;
}

/**
 * GenAI Summary component with analysis functionality
 */
export const GenAISummary: React.FC<GenAISummaryProps> = ({
  events = [],
  timelineLoading = false
}) => {
  const {
    analysis,
    isLoading,
    error,
    hasAnalysis,
    canStartAnalysis,
    startAnalysis,
    clearAnalysis
  } = useGenAISummary({
    events,
    timelineLoading,
    autoAnalyze: false // Manual trigger via button
  });

  // Container styles for width constraints
  const containerStyles = css({
    maxWidth: '100%',
    overflowX: 'hidden',
    wordWrap: 'break-word',
    overflowWrap: 'break-word'
  });

  const buttonFooterStyles = css({
    borderTop: '1px solid #e5e5e5',
    paddingTop: '16px',
    display: 'flex',
    justifyContent: 'center'
  });

  const centerButtonStyles = css({
    display: 'flex',
    justifyContent: 'center',
    minHeight: '80px',
    alignItems: 'center',
  });

  // Render loading state
  if (isLoading) {
    return (
      <Card>
        <div className={containerStyles}>
          <Column spacing="300" spacingInset="400">
            <Heading level={4}>GenAI Summary</Heading>
            <div className={centerButtonStyles}>
              <Row spacing="200" alignmentVertical="center">
                <Loader size="small" />
                <Text type="b200">Analyzing maintenance patterns...</Text>
              </Row>
            </div>
          </Column>
        </div>
      </Card>
    );
  }

  // Render error state
  if (error) {
    return (
      <Card>
        <div className={containerStyles}>
          <Column spacing="300" spacingInset="400">
            <Heading level={5}>GenAI Summary</Heading>
            <Alert type="error">
              <Text type="b100">Analysis Failed: {error}</Text>
            </Alert>
            <div className={centerButtonStyles}>
              <Button
                type="primary"
                size="small"
                onClick={startAnalysis}
                disabled={!canStartAnalysis}
              >
                Retry Analysis
              </Button>
            </div>
          </Column>
        </div>
      </Card>
    );
  }

  // Render success state with analysis results
  if (hasAnalysis && analysis) {
    return (
      <Card>
        <div className={containerStyles}>
          <Column spacing="300" spacingInset="400">
            <Heading level={5}>GenAI Summary</Heading>
            
            {analysis.success ? (
              <>
                <AnalysisResults analysis={analysis} />
                <div className={buttonFooterStyles}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={startAnalysis}
                    disabled={!canStartAnalysis}
                  >
                    Refresh Summary
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Alert type="warning">
                  <Text type="b100">No Analysis Available: {analysis.error || 'No maintenance records found for analysis'}</Text>
                </Alert>
                <div className={centerButtonStyles}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={startAnalysis}
                    disabled={!canStartAnalysis}
                  >
                    Retry Analysis
                  </Button>
                </div>
              </>
            )}
          </Column>
        </div>
      </Card>
    );
  }

  // Render initial state (no analysis yet)
  return (
    <Card>
      <div className={containerStyles}>
        <Column spacing="300" spacingInset="400">
          <Heading level={5}>GenAI Summary</Heading>
          
          {!canStartAnalysis ? (
            <Alert type="neutral">
              <Text type="b300">
                {timelineLoading 
                  ? 'Loading maintenance history...' 
                  : events.length === 0 
                    ? 'No maintenance records available for analysis'
                    : 'Select equipment and date range to start analysis'}
              </Text>
            </Alert>
          ) : (
            <div className={centerButtonStyles}>
              <Button
                type="primary"
                size="medium"
                onClick={startAnalysis}
              >
                Start Analysis
              </Button>
            </div>
          )}
        </Column>
      </div>
    </Card>
  );
};
